import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';

import { Label } from 'core/components/label';
import AddButton from 'core/components/add-button';
import RemoveButton from 'core/components/remove-button';
import greenCheckIcon from 'core/assets/svg/green-check.svg';
import editIcon from 'core/components/editable-card/edit.svg';
import useStyles from './discount-row.styles';

/**
 * One discount row on the policy Discounts card (#32231). A store-independent analog of the
 * offer-side `Discount` row: it shows the discount label and the same add/remove affordance, but
 * binds to nothing itself — the parent `FarmDiscounts` supplies `onAdd`/`onRemove`.
 *
 * Affordance (mirrors the offer row):
 *   - active + editable   → Remove button
 *   - active + read-only  → green check (applied, can't change — e.g. claim-free for a non-UW)
 *   - inactive + editable → Add button
 *   - inactive + read-only → label only
 *
 * `onEdit` adds a pencil alongside the Remove button, for the one discount that carries more than
 * an on/off state: MO Farm Associations, where the applied discount also records WHICH associations
 * the insured belongs to (#34739). The offer row does the same.
 *
 * `statusText` is shown beside the affordance, for read-only rows that state whether they apply —
 * the brand-declared auto discounts (#35626).
 */
const DiscountRow = ({ id, label, isActive, onAdd, onEdit, onRemove, disabled, statusText }) => {
  const classes = useStyles();
  return (
    <div id={id} className={classNames(classes.row, { [classes.inactive]: !isActive })}>
      <div className={classes.labelSection}>
        <Label type="discount">{label}</Label>
      </div>
      <div className={classes.section}>
        {statusText && (
          <Label type="discount" className={classes.statusText}>
            {statusText}
          </Label>
        )}
        {isActive && !onRemove && !onEdit && <img src={greenCheckIcon} alt="applied" />}
        {isActive && onEdit && (
          <IconButton onClick={onEdit} disabled={disabled} title="Edit discount" className={classes.editButton}>
            <img src={editIcon} alt="edit" className={classes.editIcon} />
          </IconButton>
        )}
        {isActive && onRemove && <RemoveButton onClick={onRemove} disabled={disabled} />}
        {!isActive && onAdd && (
          <AddButton type="full" className={classes.addButton} onClick={onAdd} disabled={disabled} />
        )}
      </div>
    </div>
  );
};

DiscountRow.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  statusText: PropTypes.string
};

DiscountRow.defaultProps = {
  isActive: false,
  onAdd: undefined,
  onEdit: undefined,
  onRemove: undefined,
  disabled: false,
  statusText: undefined
};

export default DiscountRow;
